import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import CampaignPreview from "@/components/CampaignPreview";
import {
  Megaphone,
  Sparkles,
  Users,
  Workflow,
  BarChart3,
  ShieldCheck,
  Lock,
  Plus,
  Copy,
  Eye,
  Rocket,
  Pencil,
  Trash2,
  ChevronRight,
  Mail,
  MessageSquare,
  Globe,
  Filter,
  Search,
  Zap,
  Crown,
} from "lucide-react";
import { devWarn } from "@/lib/devlog";

const TABS = [
  { id: "library", label: "Campaign Library", icon: Sparkles },
  { id: "campaigns", label: "My Campaigns", icon: Megaphone },
  { id: "audiences", label: "Audiences", icon: Users },
  { id: "automations", label: "Automations", icon: Workflow, premium: true },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

const CATEGORY_LABELS = {
  sleep: "Sleep",
  metabolic: "Metabolic",
  wellness: "Wellness",
  longevity: "Longevity",
  diagnostics: "Diagnostics",
  reactivation: "Reactivation",
  subscription: "Subscription",
  upsell: "Patient Upsell",
  acquisition: "Acquisition",
};

const CATEGORY_ACCENT = {
  sleep: "bg-indigo-50 text-indigo-700",
  metabolic: "bg-emerald-50 text-emerald-700",
  wellness: "bg-rose-50 text-rose-700",
  longevity: "bg-amber-50 text-amber-700",
  diagnostics: "bg-cyan-50 text-cyan-700",
  reactivation: "bg-orange-50 text-orange-700",
  subscription: "bg-violet-50 text-violet-700",
  upsell: "bg-blue-50 text-blue-700",
  acquisition: "bg-pink-50 text-pink-700",
};

const CHANNEL_ICONS = {
  email: Mail,
  sms: MessageSquare,
  landing_page: Globe,
  qr: Globe,
  social: Globe,
  referral: Users,
  ad: Megaphone,
};

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-700",
  scheduled: "bg-amber-50 text-amber-700",
  active: "bg-emerald-50 text-emerald-700",
  paused: "bg-slate-200 text-slate-700",
  completed: "bg-blue-50 text-blue-700",
  archived: "bg-slate-100 text-slate-500",
};

function ComplianceBanner() {
  return (
    <div
      data-testid="campaigns-compliance-banner"
      className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-3 text-sm text-amber-900 mb-6"
    >
      <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
      <div>
        <strong className="font-semibold">Marketing-only platform.</strong>{" "}
        Campaigns deployed here are educational and engagement-focused. Avoid
        medical claims, guaranteed outcomes, treatment recommendations, or
        patient-protected information. Only contact consumers with documented
        marketing authorization.
      </div>
    </div>
  );
}

function PremiumLock({ children, unlocked, onUpgrade }) {
  if (unlocked) return children;
  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none select-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[1px] rounded-2xl">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-lg p-5 max-w-sm text-center">
          <div className="inline-flex w-10 h-10 rounded-full bg-amber-50 items-center justify-center mb-2">
            <Crown className="w-5 h-5 text-amber-600" />
          </div>
          <div className="font-display text-lg font-bold text-slate-900">
            Unlock Marketing Automation
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Recurring campaigns, behavior triggers, AI assistant, and multi-channel
            sequencing are available on Growth and higher.
          </p>
          <button
            type="button"
            data-testid="campaigns-upgrade-btn"
            onClick={onUpgrade}
            className="mt-4 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
          >
            <Crown className="w-4 h-4" /> Upgrade plan
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PortalCampaigns() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "library";
  const setTab = (id) => setParams({ tab: id }, { replace: true });

  const [tier, setTier] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isPremium = tier?.tier === "premium" || tier?.tier === "enterprise";

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, tm, c, a, an] = await Promise.all([
        api.get("/clinic/campaigns/tier"),
        api.get("/clinic/campaigns/templates"),
        api.get("/clinic/campaigns"),
        api.get("/clinic/campaigns/audiences"),
        api.get("/clinic/campaigns/analytics"),
      ]);
      setTier(t.data);
      setTemplates(tm.data?.templates || []);
      setCampaigns(c.data?.campaigns || []);
      setAudiences(a.data?.audiences || []);
      setAnalytics(an.data || null);
      // Automations only fetchable on premium tiers.
      if (t.data?.tier !== "standard") {
        try {
          const au = await api.get("/clinic/campaigns/automations");
          setAutomations(au.data?.automations || []);
        } catch {
          setAutomations([]);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToolPageShell
      eyebrow="Marketing Tools"
      title="Marketing Campaigns"
      intro="Launch and automate consumer campaigns from one central platform. Educational, engagement-focused outreach for cash-pay programs."
    >
      <ComplianceBanner />

      {/* Hero strip */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 mb-6 flex items-center justify-between gap-6 flex-wrap" data-testid="campaigns-hero">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider">
            <Sparkles className="w-3 h-3" /> {tier?.plan_name || "Standard"}
          </div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
            Launch and automate consumer campaigns from one central platform.
          </h2>
          <p className="mt-2 text-sm text-slate-200">
            Deploy high-converting campaigns that help your clinic attract,
            engage, and retain consumers in the modern cash-pay healthcare economy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="campaigns-new-btn"
            onClick={() => navigate("/portal/marketing/campaigns/new")}
            className="inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-4 py-2 text-sm font-bold hover:bg-blue-50"
          >
            <Plus className="w-4 h-4" /> New campaign
          </button>
          <button
            type="button"
            data-testid="campaigns-library-btn"
            onClick={() => setTab("library")}
            className="inline-flex items-center gap-1.5 bg-white/10 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-white/20 border border-white/20"
          >
            Browse library
          </button>
        </div>
      </div>

      {/* Tab nav */}
      <div className="border-b border-slate-200 mb-5 -mx-2 px-2 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max" data-testid="campaigns-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            const locked = t.premium && tier?.tier === "standard";
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`campaigns-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-[2px] ${
                  isActive
                    ? "text-slate-900 border-slate-900"
                    : "text-slate-500 hover:text-slate-900 border-transparent"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                {locked && <Lock className="w-3 h-3 text-amber-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="text-sm text-slate-500">Loading marketing data…</div>
      )}

      {!loading && tab === "library" && (
        <LibraryTab
          templates={templates}
          onDeploy={async (tid) => {
            try {
              const r = await api.post(`/clinic/campaigns/from-template/${tid}`);
              toast.success("Campaign deployed from template — open it to customize.");
              navigate(`/portal/marketing/campaigns/${r.data.id}`);
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
        />
      )}

      {!loading && tab === "campaigns" && (
        <CampaignsTab
          campaigns={campaigns}
          onRefresh={refresh}
          onLaunch={async (cid) => {
            try {
              await api.post(`/clinic/campaigns/${cid}/launch`);
              toast.success("Campaign launched.");
              refresh();
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          onDuplicate={async (cid) => {
            try {
              await api.post(`/clinic/campaigns/${cid}/duplicate`);
              toast.success("Campaign duplicated.");
              refresh();
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          onDelete={async (cid) => {
            if (!window.confirm("Delete this campaign?")) return;
            try {
              await api.delete(`/clinic/campaigns/${cid}`);
              toast.success("Campaign deleted.");
              refresh();
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
        />
      )}

      {!loading && tab === "audiences" && (
        <AudiencesTab audiences={audiences} onRefresh={refresh} />
      )}

      {!loading && tab === "automations" && (
        <PremiumLock unlocked={isPremium} onUpgrade={() => navigate("/portal/billing")}>
          <AutomationsTab
            automations={automations}
            audiences={audiences}
            onRefresh={refresh}
          />
        </PremiumLock>
      )}

      {!loading && tab === "analytics" && (
        <AnalyticsTab analytics={analytics} campaigns={campaigns} />
      )}
    </ToolPageShell>
  );
}

// =============================================================
// Library tab
// =============================================================
function LibraryTab({ templates, onDeploy }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [favs, setFavs] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("nodera-campaign-favs") || "[]"));
    } catch {
      return new Set();
    }
  });

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      try {
        localStorage.setItem("nodera-campaign-favs", JSON.stringify([...next]));
      } catch (e) {
        devWarn("[PortalCampaigns] favs persist failed:", e);
      }
      return next;
    });
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (filter !== "all" && t.category !== filter) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.goal.toLowerCase().includes(q) ||
        (t.preview_text || "").toLowerCase().includes(q)
      );
    });
  }, [templates, search, filter]);

  return (
    <div data-testid="campaigns-library">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates by name, goal, or content…"
            className="w-full rounded-full border border-slate-200 pl-9 pr-3 py-2 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
            data-testid="campaigns-library-search"
          />
        </div>
        <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1" data-testid="campaigns-library-filter">
          <Filter className="w-3 h-3 text-slate-400 ml-2" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-xs font-semibold text-slate-700 bg-transparent border-0 focus:outline-none focus:ring-0 pr-2"
          >
            <option value="all">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <span className="text-xs text-slate-500 ml-auto">
          {visible.length} of {templates.length} templates
        </span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((t) => {
          const accent = CATEGORY_ACCENT[t.category] || "bg-slate-100 text-slate-700";
          return (
            <div
              key={t.id}
              data-testid={`campaign-template-${t.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${accent}`}>
                  {CATEGORY_LABELS[t.category] || t.category}
                </span>
                <button
                  type="button"
                  onClick={() => toggleFav(t.id)}
                  data-testid={`campaign-template-fav-${t.id}`}
                  className={`text-xs font-semibold rounded-full px-2 py-0.5 transition-colors ${
                    favs.has(t.id)
                      ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {favs.has(t.id) ? "★ Favorite" : "☆ Save"}
                </button>
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{t.name}</h3>
              <p className="mt-1 text-sm text-slate-600">{t.goal}</p>
              <div className="mt-3 text-[11px] text-slate-500">
                <strong className="text-slate-700">Audience:</strong> {t.recommended_audience}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                <strong className="text-slate-700">Engagement:</strong> {t.engagement_type}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(t.channels || []).map((ch) => {
                  const Icon = CHANNEL_ICONS[ch] || Mail;
                  return (
                    <span key={ch} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 capitalize">
                      <Icon className="w-3 h-3" /> {ch.replace("_", " ")}
                    </span>
                  );
                })}
              </div>
              {t.automation_available && (
                <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-violet-700">
                  <Zap className="w-3 h-3" /> Automation-ready
                </div>
              )}
              <div className="mt-auto pt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onDeploy(t.id)}
                  data-testid={`campaign-template-deploy-${t.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-bold hover:bg-slate-800"
                >
                  <Rocket className="w-3.5 h-3.5" /> Deploy campaign
                </button>
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
            No templates match your filters.
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================
// Campaigns list tab
// =============================================================
function CampaignsTab({ campaigns, onLaunch, onDuplicate, onDelete }) {
  const navigate = useNavigate();
  const [previewing, setPreviewing] = useState(null);
  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12 rounded-2xl border border-dashed border-slate-300 bg-white">
        <Megaphone className="w-8 h-8 text-slate-300 mx-auto" />
        <h3 className="mt-3 font-display text-lg font-bold text-slate-900">No campaigns yet</h3>
        <p className="mt-1 text-sm text-slate-600">
          Deploy from the library or build a new campaign from scratch.
        </p>
        <Link
          to="/portal/marketing/campaigns/new"
          className="mt-4 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-bold hover:bg-slate-800"
          data-testid="campaigns-empty-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New campaign
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="campaigns-list">
      {campaigns.map((c) => (
        <div
          key={c.id}
          data-testid={`campaign-row-${c.id}`}
          className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 flex-wrap hover:shadow-sm transition-shadow"
        >
          <div className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${
            CATEGORY_ACCENT[c.category] || "bg-slate-100 text-slate-700"
          }`}>
            {CATEGORY_LABELS[c.category] || c.category}
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2">
              <span className="font-display font-bold text-slate-900">{c.name}</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_PILL[c.status] || "bg-slate-100"}`}>
                {c.status}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{c.goal}</div>
          </div>
          <div className="flex flex-wrap gap-1">
            {(c.channels || []).map((ch) => {
              const Icon = CHANNEL_ICONS[ch.channel] || Mail;
              return (
                <span key={ch.channel} className="inline-flex items-center gap-0.5 text-[10px] bg-slate-100 text-slate-700 rounded-full px-1.5 py-0.5">
                  <Icon className="w-3 h-3" />
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPreviewing(c)}
              data-testid={`campaign-preview-${c.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700"
              title="Preview"
            >
              <Eye className="w-3 h-3" /> Preview
            </button>
            <button
              type="button"
              onClick={() => navigate(`/portal/marketing/campaigns/${c.id}`)}
              data-testid={`campaign-edit-${c.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700"
              title="Edit"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            {c.status === "draft" && (
              <button
                type="button"
                onClick={() => onLaunch(c.id)}
                data-testid={`campaign-launch-${c.id}`}
                className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white"
                title="Launch"
              >
                <Rocket className="w-3 h-3" /> Launch
              </button>
            )}
            <button
              type="button"
              onClick={() => onDuplicate(c.id)}
              data-testid={`campaign-duplicate-${c.id}`}
              className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
              title="Duplicate"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(c.id)}
              data-testid={`campaign-delete-${c.id}`}
              className="p-1.5 rounded-full hover:bg-rose-50 text-rose-500"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      {previewing && (
        <CampaignPreview campaign={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

// =============================================================
// Audiences tab
// =============================================================
function AudiencesTab({ audiences, onRefresh }) {
  const [creating, setCreating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", segment_tag: "" });
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    try {
      await api.post("/clinic/campaigns/audiences", form);
      toast.success("Audience created.");
      setForm({ name: "", description: "", segment_tag: "" });
      setCreating(false);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const importCsv = async (aid, file) => {
    setBusyId(aid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/clinic/campaigns/audiences/${aid}/import-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Added ${r.data.added} contacts (${r.data.skipped_duplicate} duplicates, ${r.data.skipped_suppressed} suppressed, ${r.data.skipped_missing_contact} missing email/phone).`,
      );
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (aid) => {
    if (!window.confirm("Delete this audience and all its members?")) return;
    try {
      await api.delete(`/clinic/campaigns/audiences/${aid}`);
      toast.success("Audience deleted.");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="campaigns-audiences">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display text-lg font-bold text-slate-900">Audience segments</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAiOpen(true)}
            data-testid="audiences-ai-suggest-btn"
            className="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 rounded-full px-3.5 py-1.5 text-xs font-bold hover:bg-violet-100 border border-violet-200"
          >
            <Sparkles className="w-3.5 h-3.5" /> AI: describe a segment
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="audiences-new-btn"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3.5 py-1.5 text-xs font-bold hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" /> New audience
          </button>
        </div>
      </div>

      <div
        className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-900 mb-4"
        data-testid="audiences-consent-banner"
      >
        <strong className="font-semibold">Consent required:</strong> only upload
        and engage consumers with appropriate marketing authorization. Opt-out
        and unsubscribe requests are honored automatically across this clinic.
      </div>

      {creating && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4 space-y-3" data-testid="audiences-create-form">
          <input
            type="text"
            placeholder="Audience name (e.g. Sleep interest — active patients)"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Segment tag (sleep_interest, metabolic_interest, etc.)"
            value={form.segment_tag}
            onChange={(e) => setForm((f) => ({ ...f, segment_tag: e.target.value }))}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <textarea
            rows={2}
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={create}
              data-testid="audiences-create-submit"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3.5 py-1.5 text-xs font-bold"
            >
              Create audience
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {audiences.map((a) => (
          <div
            key={a.id}
            data-testid={`audience-row-${a.id}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col gap-2"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-slate-900">{a.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {a.segment_tag ? `Tag: ${a.segment_tag}` : "Untagged segment"}
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-bold bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
                <Users className="w-3 h-3" /> {a.member_count}
              </span>
            </div>
            {a.description && <p className="text-sm text-slate-600">{a.description}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold rounded-full px-3 py-1.5 bg-slate-100 hover:bg-slate-200">
                {busyId === a.id ? "Uploading…" : "Import CSV"}
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  data-testid={`audience-import-${a.id}`}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) importCsv(a.id, f);
                  }}
                />
              </label>
              <button
                type="button"
                onClick={() => remove(a.id)}
                className="text-xs font-semibold rounded-full px-3 py-1.5 text-rose-600 hover:bg-rose-50"
                data-testid={`audience-delete-${a.id}`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {audiences.length === 0 && !creating && (
          <div className="sm:col-span-2 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
            No audiences yet. Create your first segment.
          </div>
        )}
      </div>

      {aiOpen && (
        <AudienceAIDialog
          onClose={() => setAiOpen(false)}
          onCreated={(audId, deployTemplateId) => {
            setAiOpen(false);
            onRefresh();
            if (deployTemplateId) {
              api
                .post(`/clinic/campaigns/from-template/${deployTemplateId}`)
                .then(async (r) => {
                  // Pre-fill the new draft with the audience the user just created.
                  await api.patch(`/clinic/campaigns/${r.data.id}`, { audience_id: audId });
                  toast.success("Campaign created — opening builder.");
                  navigate(`/portal/marketing/campaigns/${r.data.id}`);
                })
                .catch((e) => toast.error(formatApiError(e)));
            }
          }}
        />
      )}
    </div>
  );
}

function AudienceAIDialog({ onClose, onCreated }) {
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [audForm, setAudForm] = useState(null);

  const generate = async () => {
    if (!description.trim()) {
      toast.error("Describe the audience you want to reach.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/clinic/campaigns/audiences/ai-suggest", {
        description,
      });
      setSuggestion(r.data);
      setAudForm({
        name: r.data.audience?.name || "",
        segment_tag: r.data.audience?.segment_tag || "",
        description: r.data.audience?.description || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createAudience = async (deployTemplateId = null) => {
    if (!audForm?.name?.trim()) {
      toast.error("Audience name is required.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/clinic/campaigns/audiences", audForm);
      toast.success("Audience created.");
      onCreated(r.data.id, deployTemplateId);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-[1px] p-4"
      data-testid="audiences-ai-dialog"
    >
      <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="font-display font-bold text-slate-900 inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-violet-600" /> AI audience builder
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 text-sm"
          >
            Close
          </button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="text-xs text-slate-500">
            Describe the consumers you want to reach in plain English. The
            assistant will draft a segment and suggest matching campaign templates.
            Engagement-focused only — no medical claims, no diagnosis, no PHI.
          </div>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Existing patients who mentioned sleep issues on intake but never enrolled in a sleep program"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            data-testid="audiences-ai-description"
          />
          <button
            type="button"
            onClick={generate}
            disabled={busy}
            data-testid="audiences-ai-generate"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? "Generating…" : "Generate audience suggestion"}
          </button>

          {suggestion && audForm && (
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Suggested audience
              </div>
              <input
                type="text"
                value={audForm.name}
                onChange={(e) => setAudForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="audiences-ai-name"
              />
              <input
                type="text"
                value={audForm.segment_tag}
                onChange={(e) => setAudForm((f) => ({ ...f, segment_tag: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="audiences-ai-tag"
              />
              <textarea
                rows={2}
                value={audForm.description}
                onChange={(e) => setAudForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="audiences-ai-desc"
              />
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 pt-2">
                Recommended campaign templates
              </div>
              <div className="space-y-2">
                {(suggestion.suggested_templates || []).map((t) => (
                  <div
                    key={t.id}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center gap-3"
                    data-testid={`audiences-ai-template-${t.id}`}
                  >
                    <div
                      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        CATEGORY_ACCENT[t.category] || "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {CATEGORY_LABELS[t.category] || t.category}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-slate-900 text-sm">
                        {t.name}
                      </div>
                      <div className="text-[11px] text-slate-500 line-clamp-1">
                        {t.goal}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => createAudience(t.id)}
                      disabled={busy}
                      data-testid={`audiences-ai-deploy-${t.id}`}
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                    >
                      <Rocket className="w-3 h-3" /> Create + open builder
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => createAudience(null)}
                disabled={busy}
                data-testid="audiences-ai-save-only"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
              >
                Save audience only (don't create a campaign yet)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Automations tab
// =============================================================
const TRIGGER_LABELS = {
  form_submission: "Form submission",
  no_response: "No response",
  subscription_expiring: "Subscription expiring",
  inactive_engagement: "Inactive engagement",
  recurring_schedule: "Recurring schedule",
  vendor_workflow_completed: "Vendor workflow completed",
  abandoned_signup: "Abandoned signup",
};

function AutomationsTab({ automations, audiences, onRefresh }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    trigger_type: "form_submission",
    audience_id: "",
    enabled: false,
  });

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    try {
      await api.post("/clinic/campaigns/automations", {
        name: form.name,
        trigger_type: form.trigger_type,
        audience_id: form.audience_id || null,
        enabled: form.enabled,
        steps: [
          { type: "trigger", config: { trigger_type: form.trigger_type } },
          { type: "delay", config: { hours: 24 } },
          { type: "send_email", config: { subject: "Hello from your clinic" } },
        ],
      });
      toast.success("Automation created.");
      setForm({ name: "", trigger_type: "form_submission", audience_id: "", enabled: false });
      setCreating(false);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = async (auid, enabled) => {
    try {
      await api.patch(`/clinic/campaigns/automations/${auid}`, { enabled: !enabled });
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (auid) => {
    if (!window.confirm("Delete this automation?")) return;
    try {
      await api.delete(`/clinic/campaigns/automations/${auid}`);
      toast.success("Automation deleted.");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="campaigns-automations">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Automation flows</h3>
          <p className="text-xs text-slate-500">
            Trigger-based recurring engagement. Steps run sequentially with delays and conditions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="automations-new-btn"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3.5 py-1.5 text-xs font-bold hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> New automation
        </button>
      </div>

      {creating && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4 space-y-3" data-testid="automations-create-form">
          <input
            type="text"
            placeholder="Automation name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="grid sm:grid-cols-2 gap-2">
            <select
              value={form.trigger_type}
              onChange={(e) => setForm((f) => ({ ...f, trigger_type: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              value={form.audience_id}
              onChange={(e) => setForm((f) => ({ ...f, audience_id: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">No audience filter</option>
              {audiences.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.name} (${a.member_count})`}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={create}
            data-testid="automations-create-submit"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3.5 py-1.5 text-xs font-bold"
          >
            Create automation
          </button>
        </div>
      )}

      <div className="space-y-3">
        {automations.map((a) => (
          <div
            key={a.id}
            data-testid={`automation-row-${a.id}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col gap-3"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-bold text-slate-900">{a.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Trigger: {TRIGGER_LABELS[a.trigger_type] || a.trigger_type} · {a.steps?.length || 0} steps
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggle(a.id, a.enabled)}
                  data-testid={`automation-toggle-${a.id}`}
                  className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-1.5 ${
                    a.enabled
                      ? "bg-emerald-600 text-white hover:bg-emerald-500"
                      : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                  }`}
                >
                  {a.enabled ? "Enabled" : "Paused"}
                </button>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="p-1.5 rounded-full hover:bg-rose-50 text-rose-500"
                  data-testid={`automation-delete-${a.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {/* Visual flow */}
            <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid={`automation-flow-${a.id}`}>
              {(a.steps || []).map((s, i) => (
                <span key={s.id || i} className="inline-flex items-center gap-1 shrink-0">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold px-2.5 py-1 capitalize">
                    {s.type.replace("_", " ")}
                  </span>
                  {i < (a.steps?.length || 0) - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
                </span>
              ))}
            </div>
          </div>
        ))}
        {automations.length === 0 && !creating && (
          <div className="text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
            No automations yet. Build your first recurring flow.
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================
// Analytics tab
// =============================================================
function AnalyticsTab({ analytics, campaigns }) {
  if (!analytics) {
    return <div className="text-sm text-slate-500">Loading analytics…</div>;
  }
  const t = analytics.totals || {};
  const sentRatio = t.sent ? (n) => Math.round((n / t.sent) * 100) : () => 0;
  return (
    <div data-testid="campaigns-analytics" className="space-y-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Active campaigns" value={analytics.active_campaigns} accent="bg-emerald-500" />
        <KpiCard label="Total sent" value={t.sent || 0} accent="bg-blue-500" />
        <KpiCard label="Open rate" value={t.sent ? `${sentRatio(t.open || 0)}%` : "—"} accent="bg-violet-500" />
        <KpiCard label="Conversions" value={t.conversion || 0} accent="bg-amber-500" />
      </div>

      {/* Funnel */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Engagement funnel</div>
        <div className="space-y-2" data-testid="campaigns-analytics-funnel">
          {(analytics.funnel || []).map((f, i) => {
            const max = analytics.funnel[0]?.count || 1;
            const pct = Math.round((f.count / max) * 100) || 0;
            return (
              <div key={f.stage} className="flex items-center gap-3">
                <div className="w-24 text-xs font-semibold text-slate-700">{f.stage}</div>
                <div className="flex-1 h-6 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${
                      ["bg-blue-500", "bg-violet-500", "bg-emerald-500", "bg-amber-500"][i] || "bg-slate-400"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="w-16 text-right text-sm font-bold tabular-nums text-slate-900">{f.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Channel mix */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Channel mix</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="campaigns-analytics-channels">
          {Object.entries(analytics.channels || {}).map(([ch, count]) => {
            const Icon = CHANNEL_ICONS[ch] || Mail;
            return (
              <div key={ch} className="rounded-xl border border-slate-100 bg-slate-50 p-3 flex items-center gap-2">
                <Icon className="w-4 h-4 text-slate-500" />
                <div className="flex-1">
                  <div className="text-xs text-slate-500 capitalize">{ch.replace("_", " ")}</div>
                  <div className="font-bold text-slate-900">{count}</div>
                </div>
              </div>
            );
          })}
          {Object.keys(analytics.channels || {}).length === 0 && (
            <div className="lg:col-span-4 text-sm text-slate-500">No channel activity yet.</div>
          )}
        </div>
      </div>

      {/* Per-campaign performance */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Per-campaign performance</div>
        <div className="overflow-x-auto" data-testid="campaigns-analytics-table">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 text-left">
              <tr>
                <th className="py-2">Campaign</th>
                <th className="py-2">Sent</th>
                <th className="py-2">Opens</th>
                <th className="py-2">Clicks</th>
                <th className="py-2">Conversions</th>
                <th className="py-2">Unsubs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campaigns.map((c) => {
                const m = analytics.per_campaign?.[c.id] || {};
                return (
                  <tr key={c.id}>
                    <td className="py-2 text-slate-900 font-semibold">{c.name}</td>
                    <td className="py-2 tabular-nums">{m.sent || 0}</td>
                    <td className="py-2 tabular-nums">{m.open || 0}</td>
                    <td className="py-2 tabular-nums">{m.click || 0}</td>
                    <td className="py-2 tabular-nums">{m.conversion || 0}</td>
                    <td className="py-2 tabular-nums">{m.unsubscribe || 0}</td>
                  </tr>
                );
              })}
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-500">
                    No campaigns yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <span className={`w-1 h-10 rounded-full ${accent}`} />
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}
